import * as core from '@spyglassmc/core';
/**
 * @param inputVersion {@link core.Config.env.gameVersion}
 */
export async function resolveConfiguredVersion(inputVersion, versions, getPackMcmeta) {
    function findReleaseTarget(version) {
        if (version.release_target) {
            return version.release_target;
        }
        if (version.type === 'release') {
            return version.id;
        }
        const index = versions.findIndex((v) => v.id === version.id);
        for (let i = index; i >= 0; i -= 1) {
            if (versions[i].type === 'release') {
                return versions[i].id;
            }
        }
        // DOCS: Update this when a new snapshot cycle begins
        return '1.21.2';
    }
    function toVersionInfo(version) {
        version = version ?? versions[0];
        return {
            id: version.id,
            name: version.name,
            release: findReleaseTarget(version),
            isLatest: version === versions[0],
        };
    }
    if (versions.length === 0) {
        throw new Error('mcmeta version list is empty');
    }
    inputVersion = inputVersion.toLowerCase();
    versions = versions.sort((a, b) => b.data_version - a.data_version);
    const latestRelease = versions.find((v) => v.type === 'release');
    if (inputVersion === 'auto') {
        const packMcmeta = await getPackMcmeta();
        if (packMcmeta && latestRelease) {
            // If the pack format is larger than the latest release, use the latest snapshot
            if (packMcmeta.pack.pack_format > latestRelease.data_pack_version) {
                return toVersionInfo(versions[0]);
            }
            // Look for versions from recent to oldest, picking the most recent release that matches
            let oldestRelease = undefined;
            for (const version of versions) {
                if (version.type === 'release') {
                    // If we already passed the pack format, use the oldest release so far
                    if (packMcmeta.pack.pack_format > version.data_pack_version) {
                        return toVersionInfo(oldestRelease);
                    }
                    if (packMcmeta.pack.pack_format === version.data_pack_version) {
                        return toVersionInfo(version);
                    }
                    oldestRelease = version;
                }
            }
            // If the pack format is still lower, use the oldest known release version
            return toVersionInfo(oldestRelease);
        }
        // Fall back to the latest release if pack mcmeta is not available
        return toVersionInfo(latestRelease);
    }
    else if (inputVersion === 'latest release') {
        return toVersionInfo(latestRelease);
    }
    else if (inputVersion === 'latest snapshot') {
        return toVersionInfo(versions[0]);
    }
    return toVersionInfo(versions.find((v) => inputVersion === v.id.toLowerCase() || inputVersion === v.name.toLowerCase()));
}
const DataSources = {
    fastly: 'https://fastly.jsdelivr.net/gh/${user}/${repo}@${tag}/${path}',
    github: 'https://raw.githubusercontent.com/${user}/${repo}/${tag}/${path}',
    jsdelivr: 'https://cdn.jsdelivr.net/gh/${user}/${repo}@${tag}/${path}',
};
export function getMcmetaSummaryUris(version, isLatest, source) {
    const tag = isLatest ? 'summary' : `${version}-summary`;
    function getUri(path) {
        const template = DataSources[source.toLowerCase()] ?? source;
        const ans = template.replace(/\${user}/g, 'misode').replace(/\${repo}/g, 'mcmeta').replace(/\${tag}/g, tag).replace(/\${path}/g, path);
        if (!core.RemoteUriString.is(ans)) {
            throw new Error(`Expected a remote URI from data source template but got ${ans}`);
        }
        return ans;
    }
    return {
        blocks: getUri('blocks/data.json.gz'),
        commands: getUri('commands/data.json.gz'),
        registries: getUri('registries/data.json.gz'),
    };
}
export function symbolRegistrar(summary) {
    const McmetaSummaryUri = 'mcmeta://summary/registries.json';
    /**
     * Add states of blocks or fluids to the symbol table.
     */
    function addStatesSymbols(category, states, symbols) {
        const capitalizedCategory = `${category[0].toUpperCase()}${category.slice(1)}`;
        for (const [id, [properties, defaults]] of Object.entries(states)) {
            const uri = McmetaSummaryUri;
            symbols.query(uri, category, core.ResourceLocation.lengthen(id)).onEach(Object.entries(properties), ([state, values], blockQuery) => {
                const defaultValue = defaults[state];
                blockQuery.member(`${uri}#${capitalizedCategory}_states`, state, (stateQuery) => {
                    stateQuery.enter({
                        data: { subcategory: 'state' },
                        usage: { type: 'declaration' },
                    }).onEach(values, (value) => {
                        stateQuery.member(value, (valueQuery) => {
                            valueQuery.enter({
                                data: { subcategory: 'state_value' },
                                usage: { type: 'declaration' },
                            });
                            if (value === defaultValue) {
                                stateQuery.amend({
                                    data: { relations: { default: { category, path: valueQuery.path } } },
                                });
                            }
                        });
                    });
                });
            });
        }
    }
    function addRegistriesSymbols(registries, symbols) {
        function isCategory(str) {
            return (core.FileCategories.includes(str)
                || core.RegistryCategories.includes(str));
        }
        for (const [registryId, registry] of Object.entries(registries)) {
            if (isCategory(registryId)) {
                for (const entryId of registry) {
                    symbols.query(McmetaSummaryUri, registryId, core.ResourceLocation.lengthen(entryId))
                        .enter({ usage: { type: 'declaration' } });
                }
            }
        }
    }
    function addBuiltinSymbols(symbols) {
        symbols.query(McmetaSummaryUri, 'loot_table', 'minecraft:empty')
            .enter({ usage: { type: 'declaration' } });
    }
    return (symbols) => {
        addRegistriesSymbols(summary.registries, symbols);
        addStatesSymbols('block', summary.blocks, symbols);
        addStatesSymbols('fluid', summary.fluids, symbols);
        addBuiltinSymbols(symbols);
    };
}
export const Fluids = {
    flowing_lava: [{ falling: ['false', 'true'], level: ['1', '2', '3', '4', '5', '6', '7', '8'] }, {
            falling: 'false',
            level: '1',
        }],
    flowing_water: [
        { falling: ['false', 'true'], level: ['1', '2', '3', '4', '5', '6', '7', '8'] },
        { falling: 'false', level: '1' },
    ],
    lava: [{ falling: ['false', 'true'] }, { falling: 'false' }],
    water: [{ falling: ['false', 'true'] }, { falling: 'false' }],
};
// #endregion
//# sourceMappingURL=mcmeta.js.map